// Hand-crafted client for the CMS.Core.Backup REST API. Replaces the old
// auto-generated BackupApiFactory. When the @cms/backup-sdk package is introduced later,
// this file becomes a simple re-export.
//
// Goes through the shared `axios` instance (NOT raw fetch) so the Auth0AxiosBridge
// interceptor attaches the admin Bearer token on deployed instances : raw-fetch
// clients silently 401 there (same fix as contentByIdClient).
import axios, { AxiosProgressEvent } from "axios";

export interface BackupJobDto {
	id: string;
	kind: string;
	scope: string;
	status: string;
	isDegraded: boolean;
	destinationId: string;
	instanceId?: string | null;
	startedAt: string;
	finishedAt?: string | null;
	bytesWritten: number;
	rowsExported: number;
	error?: string | null;
	correlationId: string;
}

export interface BackupDestinationDto {
	id: string;
	kind: string;
	enabled: boolean;
	primary: boolean;
	encryption: string;
	health: string;
}

export interface BackupInstanceDto {
	instanceId: string;
	lastSeenUtc: string;
	runningJobs: number;
	holdsGcLock: boolean;
	isCurrent: boolean;
}

/** Empreinte de stockage d'UNE destination (listing de clés brut côté serveur). */
export interface BackupDestinationMetricsDto {
	destinationId: string;
	archiveCount: number;
	archiveBytes: number;
	mediaBytes: number;
	totalBytes: number;
	lastArchiveUtc?: string | null;
	lastArchiveAgeSeconds?: number | null;
	/** Non nul quand la destination était injoignable au moment du calcul. */
	error?: string | null;
}

export interface BackupMetricsDto {
	lastSuccessfulFullAgeSeconds: number | null;
	recentJobs: number;
	degradedJobs: number;
	failedJobs: number;
	/** Empreinte par destination (archives + médias) : erreur à la place des chiffres si injoignable. */
	destinations: BackupDestinationMetricsDto[];
	/** Seuil d'octets configuré par destination (0 = contrôle désactivé). */
	alertMaxDestinationBytes: number;
	/** Âge maximal configuré (heures) de la dernière sauvegarde complète avant alerte. */
	alertMaxFullAgeHours: number;
}

/** One archive actually present on a destination (read from its meta.json). */
export interface BackupArchiveDto {
	id: string;
	kind: string;
	scope: string;
	status: string;
	startedUtc: string;
	finishedUtc?: string | null;
	rowCount: number;
	bytesWritten: number;
	encryption: string;
	destinationId: string;
	/** Pinned archives are excluded from the retention sweep (imports are always pinned). */
	pinned: boolean;
	/** Producer of the archive; an "import/…" prefix marks an imported archive. */
	generator: string;
}

export interface RetentionResultDto {
	kept: string[];
	deleted: string[];
}

/**
 * Réponse 202 de POST /restore : la restauration tourne en TÂCHE DE FOND : suivre son
 * avancement (statut, lignes appliquées, erreur) via GET jobs/{jobId}.
 */
export interface RestoreAcceptedDto {
	jobId: string;
	backupId: string;
	/** "Merge" ou "Replace" (chaîne renvoyée par l'API). */
	mode: string;
}

// ---------------------------------------------------------------------------
// Media backup (api/backup/media) : monthly mirror + zip rotation of the admin
// media bucket. Serialized by the admin API in camelCase with string enums.
// ---------------------------------------------------------------------------

export type MediaJobState = "Idle" | "Running" | "Completed" | "Failed";
export type MediaJobKind = "sync" | "rotate" | "restore";

/** Snapshot of the coordinator's one-job-at-a-time slot (sync / rotate / restore). */
export interface MediaJobStatusDto {
	state: MediaJobState;
	kind?: MediaJobKind | null;
	total: number;
	/** Objects processed successfully (failures are counted separately in `failed`). */
	done: number;
	failed: number;
	currentKey?: string | null;
	startedAtUtc?: string | null;
	finishedAtUtc?: string | null;
	error?: string | null;
}

/** Live status of one mirrored month (media/months/{yyyy-MM}/status.json). */
export interface MediaMonthStatusDto {
	month: string;
	state: string;
	objectCount: number;
	totalBytes: number;
	lastSyncUtc?: string | null;
	zipSha256?: string | null;
}

export interface MediaBackupStatusDto {
	/** Current month, yyyy-MM (UTC). */
	month: string;
	monthStatus?: MediaMonthStatusDto | null;
	job: MediaJobStatusDto;
	/** False until the host registers a media source adapter (sync unavailable). */
	sourceConfigured: boolean;
	/** False until the host registers a media restore target (import unavailable). */
	targetConfigured: boolean;
}

/** One month known on the destination : live mirror ("syncing") or zipped archive. */
export interface MediaMonthSummaryDto {
	month: string;
	state: "syncing" | "compressing" | "archived" | string;
	objectCount: number;
	totalBytes: number;
	zipSizeBytes?: number | null;
	lastSyncUtc?: string | null;
}

/** Un fichier d'un mois, tel que listé par le navigateur de sauvegarde médias. */
export interface MediaMonthFileDto {
	key: string;
	size: number;
	/** Absent dans le listing actuel de l'API : gardé optionnel par tolérance de contrat. */
	contentType?: string | null;
}

/**
 * Listing paginé des fichiers d'un mois. `source` distingue le mois courant (miroir
 * d'objets, "live") du mois roté ("archive" : entrées lues dans le répertoire central
 * du zip, jamais une extraction). `total` = nombre de fichiers AVANT pagination.
 */
export interface MediaMonthFileListingDto {
	month: string;
	source: "live" | "archive" | string;
	total: number;
	files: MediaMonthFileDto[];
}

/** Result of a media upload dry run: what the zip would restore, without writing. */
export interface MediaUploadDryRunDto {
	entries: number;
	totalBytes: number;
}

export interface BackupClient {
	listJobs(opts?: { skip?: number; take?: number }): Promise<BackupJobDto[]>;
	getJob(id: string): Promise<BackupJobDto>;
	createJob(body: { kind: string; scope: string; correlationId?: string }): Promise<BackupJobDto>;
	listDestinations(): Promise<BackupDestinationDto[]>;
	listInstances(): Promise<BackupInstanceDto[]>;
	getMetrics(): Promise<BackupMetricsDto>;
	listArchives(destinationId?: string): Promise<BackupArchiveDto[]>;
	/** Streams the raw archive container and triggers a browser download. */
	downloadArchive(id: string, destinationId?: string): Promise<void>;
	/** Uploads a previously downloaded .cbak / .cbak.enc archive; imported archives arrive pinned. */
	uploadArchive(
		file: File,
		opts?: { destinationId?: string; overwrite?: boolean; onUploadProgress?: (event: AxiosProgressEvent) => void }
	): Promise<BackupArchiveDto>;
	deleteArchive(id: string, destinationId?: string): Promise<{ id: string; filesDeleted: number }>;
	applyRetention(destinationId?: string): Promise<RetentionResultDto>;
	/**
	 * Lance une restauration en tâche de fond (202) : suivre via getJob(jobId). L'en-tête
	 * X-Confirm-Destructive est exigé par l'API pour les DEUX modes et envoyé ici.
	 */
	restore(body: { backupId: string; destinationId: string; mode: "merge" | "replace"; tables?: string[]; ids?: Record<string, string[]> }): Promise<RestoreAcceptedDto>;
	// --- Media backup ---
	/** Current month + coordinator job snapshot + source/target configuration flags. */
	mediaStatus(): Promise<MediaBackupStatusDto>;
	/** Every month known on the destination, newest first. */
	listMediaMonths(): Promise<MediaMonthSummaryDto[]>;
	/**
	 * Navigateur de sauvegarde médias, côté listing : les fichiers d'un mois, paginés
	 * (mois vif = objets du miroir ; mois roté = entrées du zip). Le téléchargement du
	 * zip mensuel complet a été RETIRÉ de l'API (410) : c'est ce chemin qui le remplace.
	 */
	listMediaMonthFiles(month: string, opts?: { skip?: number; take?: number }): Promise<MediaMonthFileListingDto>;
	/** Télécharge UN fichier d'un mois (blob authentifié via axios, puis enregistrement navigateur). */
	downloadMediaMonthFile(month: string, key: string): Promise<void>;
	/** Récupère UN fichier d'un mois en Blob authentifié (aperçu visualiseur : l'appelant gère
	 *  l'object URL et sa révocation). */
	fetchMediaMonthFileBlob(month: string, key: string): Promise<Blob>;
	/** Starts the monthly sync (202 = accepted; 409 = a media job is already running). */
	startMediaSync(): Promise<MediaJobStatusDto>;
	/** Starts a rotation pass alone (zip + verify + delete of past months). */
	startMediaRotate(): Promise<MediaJobStatusDto>;
	/**
	 * Imports a media zip back into the live bucket (DESTRUCTIVE : overwrites live keys;
	 * the mandatory X-Confirm-Destructive header is sent by this client).
	 * With dryRun=true the server only validates and returns { entries, totalBytes }.
	 */
	uploadMediaZip(
		file: File,
		opts?: { dryRun?: boolean; onUploadProgress?: (event: AxiosProgressEvent) => void }
	): Promise<MediaUploadDryRunDto | MediaJobStatusDto>;
	/** Snapshot of the current/last media job : poll while a job is running. */
	getMediaJobStatus(): Promise<MediaJobStatusDto>;
}

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
const base = `${apiBase}/api/backup`;
const mediaBase = `${base}/media`;

const config = { withCredentials: true } as const;

/** Hands a blob to the browser as a named download via a transient anchor click. */
function triggerBlobDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	try {
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = filename;
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
	} finally {
		// Revoke on the next tick so the click has started the download.
		window.setTimeout(() => URL.revokeObjectURL(url), 0);
	}
}

export function createBackupClient(): BackupClient {
	const api: BackupClient = {
		listJobs: async (opts) => {
			const r = await axios.get<BackupJobDto[]>(`${base}/jobs`, {
				...config,
				params: { skip: opts?.skip, take: opts?.take },
			});
			return r.data ?? [];
		},
		getJob: async (id) => (await axios.get<BackupJobDto>(`${base}/jobs/${encodeURIComponent(id)}`, config)).data,
		createJob: async (body) => (await axios.post<BackupJobDto>(`${base}/jobs`, body, config)).data,
		listDestinations: async () => (await axios.get<BackupDestinationDto[]>(`${base}/destinations`, config)).data ?? [],
		listInstances: async () => (await axios.get<BackupInstanceDto[]>(`${base}/instances`, config)).data ?? [],
		getMetrics: async () => (await axios.get<BackupMetricsDto>(`${base}/metrics`, config)).data,
		listArchives: async (destinationId) => {
			const r = await axios.get<BackupArchiveDto[]>(`${base}/archives`, {
				...config,
				params: destinationId ? { destinationId } : undefined,
			});
			return r.data ?? [];
		},
		downloadArchive: async (id, destinationId) => {
			const r = await axios.get<Blob>(`${base}/archives/${encodeURIComponent(id)}/download`, {
				...config,
				params: destinationId ? { destinationId } : undefined,
				responseType: "blob",
			});
			// Prefer the server's filename (carries the .enc suffix for encrypted archives).
			const disposition = (r.headers?.["content-disposition"] as string | undefined) ?? "";
			const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
			const filename = match ? decodeURIComponent(match[1]) : `bcc-sql-${id}.cbak`;
			triggerBlobDownload(r.data, filename);
		},
		uploadArchive: async (file, opts) => {
			const form = new FormData();
			form.append("file", file);
			const r = await axios.post<BackupArchiveDto>(`${base}/archives/upload`, form, {
				...config,
				params: {
					...(opts?.destinationId ? { destinationId: opts.destinationId } : {}),
					...(opts?.overwrite ? { overwrite: true } : {}),
				},
				onUploadProgress: opts?.onUploadProgress,
			});
			return r.data;
		},
		deleteArchive: async (id, destinationId) => {
			const r = await axios.delete<{ id: string; filesDeleted: number }>(
				`${base}/archives/${encodeURIComponent(id)}`,
				{
					...config,
					params: destinationId ? { destinationId } : undefined,
					headers: { "X-Confirm-Destructive": "I understand" },
				}
			);
			return r.data;
		},
		applyRetention: async (destinationId) => {
			const r = await axios.post<RetentionResultDto>(`${base}/retention/apply`, null, {
				...config,
				params: destinationId ? { destinationId } : undefined,
			});
			return r.data;
		},
		restore: async (body) => {
			// L'API exige l'en-tête de confirmation pour les DEUX modes (un merge est un
			// upsert massif sur toute la base) et répond 202 { jobId, backupId, mode } : la
			// restauration tourne en tâche de fond, suivie via GET jobs/{jobId}.
			const r = await axios.post<RestoreAcceptedDto>(`${base}/restore`, body, {
				...config,
				headers: { "X-Confirm-Destructive": "I understand" },
			});
			return r.data;
		},
		mediaStatus: async () => (await axios.get<MediaBackupStatusDto>(`${mediaBase}/status`, config)).data,
		listMediaMonths: async () => (await axios.get<MediaMonthSummaryDto[]>(`${mediaBase}/months`, config)).data ?? [],
		listMediaMonthFiles: async (month, opts) => {
			const r = await axios.get<MediaMonthFileListingDto>(
				`${mediaBase}/months/${encodeURIComponent(month)}/files`,
				{
					...config,
					params: { skip: opts?.skip ?? 0, take: opts?.take ?? 200 },
				}
			);
			return r.data;
		},
		fetchMediaMonthFileBlob: async (month, key) => {
			// La route est un catch-all {**key} : les « / » de la clé restent des séparateurs,
			// chaque segment est encodé individuellement.
			const encodedKey = key.split("/").map(encodeURIComponent).join("/");
			const r = await axios.get<Blob>(`${mediaBase}/months/${encodeURIComponent(month)}/files/${encodedKey}`, {
				...config,
				responseType: "blob",
			});
			return r.data;
		},
		downloadMediaMonthFile: async (month, key) => {
			const blob = await api.fetchMediaMonthFileBlob(month, key);
			triggerBlobDownload(blob, key.split("/").pop() || key);
		},
		startMediaSync: async () => (await axios.post<MediaJobStatusDto>(`${mediaBase}/sync`, null, config)).data,
		startMediaRotate: async () => (await axios.post<MediaJobStatusDto>(`${mediaBase}/rotate`, null, config)).data,
		uploadMediaZip: async (file, opts) => {
			const form = new FormData();
			form.append("file", file);
			const r = await axios.post<MediaUploadDryRunDto | MediaJobStatusDto>(`${mediaBase}/upload`, form, {
				...config,
				params: opts?.dryRun ? { dryRun: true } : undefined,
				headers: { "X-Confirm-Destructive": "I understand" },
				onUploadProgress: opts?.onUploadProgress,
			});
			return r.data;
		},
		getMediaJobStatus: async () => (await axios.get<MediaJobStatusDto>(`${mediaBase}/jobs/status`, config)).data,
	};
	return api;
}
